import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import useCafeteriaSelectionApi from './config';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import Button from '../../components/ui/Button';

const Container = styled.div``;

const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  width: 90%;
  max-width: 720px;
  max-height: 80vh;
  overflow-y: auto;
`;

const ItemTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  margin-top: 16px;
  th, td {
    padding: 8px 12px;
    text-align: left;
    border-bottom: 1px solid #e5e7eb;
  }
  th { background: #f9fafb; font-weight: 600; color: #6b7280; }
`;

const InfoLine = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f3f4f6;
  font-size: 14px;
`;

export default function CafeteriaSelectionsPage() {
  const { t } = useTranslation();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [selectedDetail, setSelectedDetail] = useState(null);

  const { columns: baseColumns, fetching, data, pagination, fetchById } = useCafeteriaSelectionApi(queryParams);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const handleViewDetails = useCallback(async (row) => {
    const detail = await fetchById(row.id);
    if (detail) setSelectedDetail(detail);
  }, [fetchById]);

  const actionOptions = useMemo(() => [
    { type: 'details', label: t('cafeteriaSelection.viewDetails'), onClick: handleViewDetails },
  ], [t, handleViewDetails]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <OptionsMenu actions={actionOptions} row={row} />;
        },
      },
    },
  ], [baseColumns, actionOptions, data]);

  return (
    <Container>
      {useMemo(() => (
        <DataTable
          title={t('cafeteriaSelection.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination, filterList,
        handleFilterConfirm, handleDataStateChange,
      ])}

      {selectedDetail && (
        <Modal onClick={() => setSelectedDetail(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>{selectedDetail.employeeFullName}</h3>
            <InfoLine>
              <span>{t('cafeteriaSelection.period')}:</span>
              <span>{selectedDetail.openEnrollmentPeriodName}</span>
            </InfoLine>
            <InfoLine>
              <span>{t('cafeteriaSelection.status')}:</span>
              <span>{t(`cafeteriaSelection.status${selectedDetail.status?.charAt(0).toUpperCase()}${selectedDetail.status?.slice(1)}`, { defaultValue: selectedDetail.status })}</span>
            </InfoLine>
            <InfoLine>
              <span>{t('cafeteriaSelection.totalAllocated')}:</span>
              <span>{Number(selectedDetail.totalAllocated ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</span>
            </InfoLine>
            <InfoLine>
              <span>{t('cafeteriaSelection.budgetAtSubmission')}:</span>
              <span>{Number(selectedDetail.budgetAtSubmission ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</span>
            </InfoLine>
            <InfoLine>
              <span>{t('cafeteriaSelection.currentBudget')}:</span>
              <span>{selectedDetail.currentBudget != null ? `${Number(selectedDetail.currentBudget).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺` : '-'}</span>
            </InfoLine>
            {selectedDetail.notes && (
              <p style={{ color: '#6b7280', fontStyle: 'italic', marginTop: 12 }}>{selectedDetail.notes}</p>
            )}

            <ItemTable>
              <thead>
                <tr>
                  <th>{t('cafeteriaSelection.itemBenefit')}</th>
                  <th>{t('cafeteriaSelection.itemPointCost')}</th>
                  <th>{t('cafeteriaSelection.itemAllocated')}</th>
                </tr>
              </thead>
              <tbody>
                {selectedDetail.items.length === 0 && (
                  <tr><td colSpan={3} style={{ textAlign: 'center', color: '#9ca3af', padding: 16 }}>{t('cafeteriaSelection.noItems')}</td></tr>
                )}
                {selectedDetail.items.map((it) => (
                  <tr key={it.id}>
                    <td>{it.benefitName}</td>
                    <td>{Number(it.pointCostSnapshot).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
                    <td>{Number(it.allocatedAmount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
            </ItemTable>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <Button onClick={() => setSelectedDetail(null)}>{t('common.close')}</Button>
            </div>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
}
